<template>
  <section class="portal">
    <modatta-sidenav page="saas"></modatta-sidenav>
    <main>
      <div class="saas components-wrapper">
        <modatta-navbar v-bind:title="$t('saas.title')" :has-icon="{ name: 'dashboard' }"></modatta-navbar>
        <div class="components">
          <div v-if="isLoading" class="loading component main-table">
            <div class="is-loading loading-row" v-for="(row, index) in 7" :key="index"></div>
          </div>
          <div v-else-if="saasList.length === 0" class="component main-table">
            <div class="modatta-empty-screen">
              <div class="empty-screen-card">
                <div
                  class="illustration"
                  :style="{ 'background-image': 'url(/svg/decorations/empty-screen-brands.svg)' }"
                ></div>
                <div class="card-info">
                  <p>{{ $t('saas.empty_message') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="component saas-list">
            <div
              v-for="(saas, index) in saasList"
              :key="saas.id"
              class="modatta-accordion"
              :class="{ 'is-open': expandedItems[index] }"
            >
              <div class="accordion-header" @click="toggleExpanded(index)">
                <div class="start">
                  <div class="logo-wrapper" :style="`background-image: url(${saas.logo})`"></div>
                  <div>
                    <div class="name">{{ saas.name }}</div>
                    <div class="description">{{ shortenSaasId(saas.id) }}</div>
                  </div>
                </div>
                <modatta-icon icon="arrow-down"></modatta-icon>
              </div>
              <div class="accordion-body">
                <div class="saas-details">
                  <!-- Basic Info -->
                  <div class="detail-section">
                    <div class="section-name">{{ $t('saas.detail_section_basic') }}</div>
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('saas.detail_name') }}:</span>
                      <span class="detail-value">{{ saas.name }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('saas.detail_id') }}:</span>
                      <span class="detail-value">{{ saas.id }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('saas.detail_url') }}:</span>
                      <a :href="saas.url" target="_blank" rel="noopener noreferrer" class="url-link">
                        {{ saas.url }}
                      </a>
                    </div>
                    <div class="detail-row" v-if="saas.logo2">
                      <span class="detail-label">{{ $t('saas.detail_logo2') }}:</span>
                      <div class="logo2-wrapper" :style="`background-image: url(${saas.logo2})`"></div>
                    </div>
                  </div>

                  <!-- Pages -->
                  <div class="detail-section" v-if="saas.page_map && saas.page_map.length > 0">
                    <div class="section-name">{{ $t('saas.detail_section_pages') }}</div>
                    <div class="page-tags">
                      <span v-for="(page, pageIndex) in saas.page_map" :key="pageIndex" class="tag">
                        {{ page }}
                      </span>
                    </div>
                  </div>

                  <!-- Colors -->
                  <div class="detail-section" v-if="saas.colour_map && Object.keys(saas.colour_map).length > 0">
                    <div class="section-name">{{ $t('saas.detail_section_colors') }}</div>
                    <div class="color-grid">
                      <div
                        v-for="(color, key) in saas.colour_map"
                        :key="key"
                        class="color-item"
                      >
                        <div class="color-swatch" :style="{ backgroundColor: color }"></div>
                        <span class="color-label">{{ key }}</span>
                        <span class="color-value">{{ color }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="detail-section" v-if="saas.content && Object.keys(saas.content).length > 0">
                    <div class="section-name">{{ $t('saas.detail_section_content') }}</div>
                    <div class="content-grid">
                      <div
                        v-for="(value, key) in saas.content"
                        :key="key"
                        class="content-item"
                      >
                        <span class="content-label">{{ formatContentKey(key) }}:</span>
                        <span class="content-value">{{ value }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Dates -->
                  <div class="detail-section">
                    <div class="section-name">{{ $t('saas.detail_section_dates') }}</div>
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('saas.detail_created') }}:</span>
                      <span class="detail-value">{{ formatDate(saas.created_at) }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">{{ $t('saas.detail_updated') }}:</span>
                      <span class="detail-value">{{ formatDate(saas.updated_at) }}</span>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="detail-section">
                    <div class="saas-actions" :style="{ display: 'flex', flexDirection: 'row', alignItems: 'center' }">
                      <button 
                        class="action-button action-edit" 
                        type="button"
                        :style="{ 
                          width: '56px', 
                          height: '56px', 
                          borderRadius: '14px', 
                          border: '2px solid #2355c3', 
                          backgroundColor: '#ffffff',
                          marginRight: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }"
                      >
                        <modatta-icon icon="edit" :width="32" :height="32"></modatta-icon>
                      </button>
                      <button 
                        class="action-button action-statistics" 
                        type="button"
                        @click="goToStatistics(saas.id)"
                        :style="{ 
                          width: '56px', 
                          height: '56px', 
                          borderRadius: '14px', 
                          border: '2px solid #2355c3', 
                          backgroundColor: '#ffffff',
                          marginRight: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }"
                      >
                        <modatta-icon icon="activity" :width="32" :height="32"></modatta-icon>
                      </button>
                      <button 
                        class="action-button action-offers" 
                        type="button"
                        :style="{ 
                          width: '56px', 
                          height: '56px', 
                          borderRadius: '14px', 
                          border: '2px solid #2355c3', 
                          backgroundColor: '#ffffff',
                          marginRight: '0',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }"
                      >
                        <modatta-icon icon="gift" :width="32" :height="32"></modatta-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
  import { mapState } from 'vuex';
  import ModattaSidenav from '../../components/ModattaSidenav.vue';
  import ModattaNavbar from '../../components/ModattaNavbar.vue';
  import ModattaIcon from '../../components/ModattaIcon.vue';
  import SaasAPI from '../../api/saas_api.js';

  export default {
    components: { ModattaSidenav, ModattaNavbar, ModattaIcon },
    data() {
      return {
        isLoading: true,
        saasList: [],
        expandedItems: {},
      };
    },
    mounted() {
      this.loadSaasList();
    },
    computed: {
      ...mapState(['user']),
    },
    methods: {
      async loadSaasList() {
        try {
          this.isLoading = true;
          const response = await SaasAPI.getSaasList();
          console.log('SAAS List API Response:', response.data);
          this.saasList = response.data.result || [];
          // Initialize all items as collapsed
          this.expandedItems = {};
        } catch (error) {
          console.error('Error loading SAAS list:', error);
        } finally {
          this.isLoading = false;
        }
      },
      toggleExpanded(index) {
        this.$set(this.expandedItems, index, !this.expandedItems[index]);
      },
      shortenSaasId(id) {
        if (!id) return '';
        return id.substring(0, 8).toUpperCase();
      },
      formatDate(dateString) {
        if (!dateString) return '-';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString();
        } catch (e) {
          return dateString;
        }
      },
      formatContentKey(key) {
        // Convert snake_case to Title Case
        return key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
      goToStatistics(saasId) {
        this.$router.push(`/saas/${saasId}/statistics`);
      },
    },
  };
</script>

<style lang="scss">
  .saas.components-wrapper {
    .components {
      overflow-y: auto !important;
      overflow-x: hidden !important;
      max-height: calc(100vh - 120px) !important;
    }
  }
  
  .saas-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .modatta-accordion {
    border: 1px solid #dee5f6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &.is-open {
      border-color: #2355c3;
      box-shadow: 0 2px 6px rgba(35, 85, 195, 0.1);
    }
  }

  .logo-wrapper {
    width: 40px;
    height: 40px;
    min-width: 40px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 4px;
  }

  .logo2-wrapper {
    width: 100px;
    height: 60px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 4px;
    margin-top: 8px;
  }

  .url-link {
    color: #2355c3;
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }

  .saas-details {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .detail-section {
    .section-name {
      font-family: 'Silka Semibold';
      font-size: 14px;
      margin-bottom: 12px;
      color: #333;
    }
  }

  .detail-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;

    .detail-label {
      font-family: 'Silka Semibold';
      font-size: 13px;
      color: #666;
      min-width: 120px;
    }

    .detail-value {
      font-size: 13px;
      color: #333;
      word-break: break-all;
    }
  }

  .page-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .color-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }

  .color-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .color-swatch {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    display: inline-block;
  }

  .color-label {
    font-family: 'Silka Semibold';
    font-size: 12px;
    color: #666;
    text-transform: capitalize;
    min-width: 80px;
  }

  .color-value {
    font-size: 12px;
    color: #333;
    font-family: monospace;
  }

  .content-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .content-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .content-label {
      font-family: 'Silka Semibold';
      font-size: 12px;
      color: #666;
      text-transform: capitalize;
    }

  .content-value {
    font-size: 13px;
    color: #333;
    line-height: 1.5;
  }

  div.saas.components-wrapper .saas-details .detail-section .saas-actions {
    display: flex !important;
    align-items: center;
    margin-top: 8px;
    gap: 0 !important;
  }

  div.saas.components-wrapper .saas-details .detail-section .saas-actions button.action-button {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;
    border-radius: 14px !important;
    border: 2px solid #2355c3 !important;
    border-color: #2355c3 !important;
    border-style: solid !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    padding: 0 !important;
    padding-block: 0 !important;
    padding-inline: 0 !important;
    margin: 0 !important;
    margin-right: 32px !important;
    margin-left: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
    font-size: 0 !important;
    line-height: 0 !important;
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;

    &:last-child {
      margin-right: 0 !important;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(35, 85, 195, 0.2) !important;
      background: rgba(35, 85, 195, 0.05) !important;
      background-color: rgba(35, 85, 195, 0.05) !important;
      border-color: #2355c3 !important;
    }

    &:active,
    &:focus {
      transform: translateY(0);
      outline: none !important;
      border-color: #2355c3 !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
      box-shadow: none !important;
    }

    .modatta-icon {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      min-height: 32px !important;

      img {
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
      }

      svg {
        width: 32px !important;
        height: 32px !important;
      }
    }

    &.action-edit,
    &.action-statistics,
    &.action-offers {
      border-color: #2355c3 !important;
      color: #2355c3 !important;
      background: #ffffff !important;
      background-color: #ffffff !important;

      &:hover {
        border-color: #2355c3 !important;
        color: #2355c3 !important;
        background: rgba(35, 85, 195, 0.05) !important;
        background-color: rgba(35, 85, 195, 0.05) !important;
      }
    }
  }
}
</style>

